import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
    Stack,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import useFetch from '../../../../hooks/useFetch';
import api from '../../../../config/axiosConfig';
import { toPersianDigits, toEnglishDigits } from '../../../../utils/convertNumbers';

const API_ENDPOINT = '/institutes/api/v1/teacher-working-days/institute-settings/';

const MINUTES_PER_DAY = 24 * 60;

// Parse an "HH:MM" value (Persian or English digits) into minutes-from-midnight.
function parseTimeToMinutes(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(toEnglishDigits(value || ''));
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
}

// A single time field (one dropdown) listing the day in 30-minute steps.
// Replaces the hour + minute pair so each side of the range is one field.
function SingleTimeSelect({ value, onChange }) {
    const current = toEnglishDigits(value || '');
    const options = useMemo(() => {
        const list = [];
        for (let m = 0; m < MINUTES_PER_DAY; m += 30) {
            const hh = String(Math.floor(m / 60)).padStart(2, '0');
            const mm = String(m % 60).padStart(2, '0');
            list.push(`${hh}:${mm}`);
        }
        return list;
    }, []);
    // Keep an off-grid saved value (e.g. 08:15) selectable.
    const allOptions = current && !options.includes(current) ? [current, ...options] : options;

    return (
        <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
                value={current}
                displayEmpty
                onChange={(e) => onChange(e.target.value)}
                renderValue={(v) => (v ? toPersianDigits(v) : '--:--')}
                sx={{ height: 48, direction: 'rtl', fontSize: '1.1rem', fontWeight: 'bold' }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
                {allOptions.map((t) => (
                    <MenuItem key={t} value={t}>{toPersianDigits(t)}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function WorkHoursManagement() {
    const theme = useTheme();
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [conflictError, setConflictError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [workHoursStart, setWorkHoursStart] = useState('');
    const [workHoursEnd, setWorkHoursEnd] = useState('');

    // Fetch current work hours
    const { data: workHoursData, loading, error, refetch } = useFetch(API_ENDPOINT);

    // Update local state when data is fetched
    React.useEffect(() => {
        if (workHoursData) {
            setWorkHoursStart(workHoursData.work_hours_start || '');
            setWorkHoursEnd(workHoursData.work_hours_end || '');
        }
    }, [workHoursData]);

    const handleSave = useCallback(async () => {
        // Validate times
        if (!workHoursStart || !workHoursEnd) {
            setSnackbar({
                open: true,
                message: 'لطفاً هر دو زمان شروع و پایان را وارد کنید.',
                severity: 'error',
            });
            return;
        }

        // Convert to English digits for API
        const startTime = toEnglishDigits(workHoursStart);
        const endTime = toEnglishDigits(workHoursEnd);

        // Validate time format
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) {
            setSnackbar({
                open: true,
                message: 'فرمت زمان نامعتبر است. لطفاً از فرمت HH:MM استفاده کنید.',
                severity: 'error',
            });
            return;
        }

        // Validate that start time is before end time
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        if (startMinutes >= endMinutes) {
            setSnackbar({
                open: true,
                message: 'ساعت شروع باید قبل از ساعت پایان باشد.',
                severity: 'error',
            });
            return;
        }

        setIsSaving(true);

        try {
            await api.patch(API_ENDPOINT, {
                work_hours_start: startTime,
                work_hours_end: endTime,
            });
            setSnackbar({
                open: true,
                message: 'ساعت کاری با موفقیت به‌روزرسانی شد.',
                severity: 'success',
            });
            await refetch();
        } catch (error) {
            const errorData = error?.response?.data || {};
            
            let errorMessage = errorData.detail 
                || errorData.error 
                || errorData.work_hours_start?.[0]
                || errorData.work_hours_end?.[0]
                || (Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors)
                || 'خطا در به‌روزرسانی ساعت کاری';
            
            // Handle work hours conflict with detailed information
            // Note: DRF serializes ValidationError fields as arrays, so we need to extract values
            // See WorkHoursSerializer.validate() in backend for error structure
            const errorCodeRaw = errorData.error_code;
            const totalConflictsRaw = errorData.total_conflicts;
            const conflicts = errorData.conflicts;
            
            // Extract values from arrays (DRF ValidationError serializes custom fields as arrays)
            const errorCode = Array.isArray(errorCodeRaw) ? errorCodeRaw[0] : errorCodeRaw;
            const totalConflicts = Array.isArray(totalConflictsRaw) 
                ? parseInt(totalConflictsRaw[0], 10) 
                : (typeof totalConflictsRaw === 'number' ? totalConflictsRaw : parseInt(totalConflictsRaw, 10));
            
            // Check if we have conflicts - if so, treat it as work hours conflict
            const hasWorkHoursConflictMessage = errorMessage && errorMessage.includes('ثبت‌نام فعال خارج از ساعت کاری');
            const hasConflicts = conflicts && Array.isArray(conflicts) && conflicts.length > 0;
            const isWorkHoursConflict = errorCode === 'work_hours_conflict' || hasWorkHoursConflictMessage;
            
            if (isWorkHoursConflict && hasConflicts) {
                setConflictError({
                    message: errorMessage,
                    conflicts: conflicts,
                    totalConflicts: totalConflicts || conflicts.length,
                });
                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: 'error',
                });
            } else {
                // Clear any previous conflict error
                setConflictError(null);
                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: 'error',
                });
            }
        } finally {
            setIsSaving(false);
        }
    }, [workHoursStart, workHoursEnd, refetch]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {error || 'خطا در بارگذاری ساعت کاری موسسه'}
                </Alert>
            </Box>
        );
    }

    const startMins = parseTimeToMinutes(workHoursStart);
    const endMins = parseTimeToMinutes(workHoursEnd);
    const isValidRange = startMins != null && endMins != null && endMins > startMins;

    // Only enable saving when the values differ from what's currently stored.
    const hasChanges =
        toEnglishDigits(workHoursStart) !== toEnglishDigits(workHoursData?.work_hours_start || '') ||
        toEnglishDigits(workHoursEnd) !== toEnglishDigits(workHoursData?.work_hours_end || '');

    return (
        <Box>
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                        <AccessTimeIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            تنظیم ساعت کاری آموزشگاه
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: { xs: 2, sm: 4 },
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography sx={{ m: 0, mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
                                ساعت شروع کار
                            </Typography>
                            <SingleTimeSelect
                                value={workHoursStart}
                                onChange={(value) => setWorkHoursStart(value)}
                            />
                        </Box>

                        <Typography sx={{ m: 0, mt: 3.5, fontWeight: 'bold', color: 'text.secondary' }}>
                            تا
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography sx={{ m: 0, mb: 1, fontWeight: 'medium', color: 'text.secondary' }}>
                                ساعت پایان کار
                            </Typography>
                            <SingleTimeSelect
                                value={workHoursEnd}
                                onChange={(value) => setWorkHoursEnd(value)}
                            />
                        </Box>
                    </Box>

                    {startMins != null && endMins != null && !isValidRange && (
                        <Typography
                            sx={{ mt: 1.5, textAlign: 'center', color: theme.palette.error.main, fontWeight: 'medium' }}
                        >
                            ساعت پایان باید بعد از ساعت شروع باشد.
                        </Typography>
                    )}

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={isSaving || !isValidRange || !hasChanges}
                            size="large"
                        >
                            {isSaving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                        </Button>
                    </Box>

                    {workHoursStart && workHoursEnd && (
                        <Box
                            sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: '0.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            }}
                        >
                            <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
                            <Typography sx={{ m: 0, fontWeight: 'medium' }}>
                                ساعت کاری فعلی
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <Typography
                                sx={{ m: 0, fontWeight: 'bold', fontSize: '1.1rem', color: theme.palette.primary.main }}
                            >
                                {toPersianDigits(workHoursStart)} تا {toPersianDigits(workHoursEnd)}
                            </Typography>
                        </Box>
                    )}

                    {conflictError && (
                        <Alert severity="error" sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {conflictError.message}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                                ثبت‌نام‌های متأثر:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                                {conflictError.conflicts.slice(0, 10).map((conflict, idx) => (
                                    <li key={idx} style={{ marginBottom: '0.5rem' }}>
                                        <Typography variant="body2">
                                            <strong>{conflict.student}</strong> - {conflict.course}
                                            {conflict.teacher && ` (مدرس: ${conflict.teacher})`}
                                            <br />
                                            <span style={{ color: theme.palette.text.secondary }}>
                                                {conflict.day} - {toPersianDigits(conflict.start_time)} تا {toPersianDigits(conflict.end_time)}
                                            </span>
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                            {conflictError.totalConflicts > 10 && (
                                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                    و {conflictError.totalConflicts - 10} مورد دیگر...
                                </Typography>
                            )}
                            <Typography variant="body2" sx={{ mt: 2, fontWeight: 'medium' }}>
                                لطفاً ابتدا این ثبت‌نام‌ها را بایگانی کنید یا زمان آن‌ها را تغییر دهید.
                            </Typography>
                        </Alert>
                    )}
                </Paper>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={handleCloseSnackbar} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
        </Box>
    );
}

export default WorkHoursManagement;

